/**
 * TimeRangePicker Component
 *
 * Allows users to select a time range with start/end times and quick duration buttons.
 * Used in ScheduleGanttDialog for adjusting the visible time range.
 */

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  ButtonGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, differenceInDays, differenceInHours, differenceInMinutes } from "date-fns";
import React, { useMemo } from "react";
import { SCHEDULE_MAX_DATE, SCHEDULE_MIN_DATE } from "../../../../utils/dateConstants";

interface TimeRangePickerProps {
  startTime: Date;
  endTime: Date;
  onChange: (startTime: Date, endTime: Date) => void;
}

/**
 * Format duration in human-readable format
 */
function formatDuration(start: Date, end: Date): string {
  const days = differenceInDays(end, start);
  const hours = differenceInHours(end, start) % 24;
  const minutes = differenceInMinutes(end, start) % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}


export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  startTime,
  endTime,
  onChange,
}) => {
  // Calculate duration
  const duration = useMemo(
    () => formatDuration(startTime, endTime),
    [startTime, endTime]
  );

  // Handle start time change
  const handleStartChange = (newValue: Date | null) => {
    if (newValue) {
      onChange(newValue, endTime);
    }
  };

  // Handle end time change
  const handleEndChange = (newValue: Date | null) => {
    if (newValue) {
      onChange(startTime, newValue);
    }
  };

  // Quick duration buttons
  const handleQuickDuration = (days: number) => {
    const newEnd = addDays(startTime, days);
    onChange(startTime, newEnd);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {/* Start Time */}
        <DateTimePicker
          label="Start Time"
          value={startTime}
          onChange={handleStartChange}
          minDate={SCHEDULE_MIN_DATE}
          maxDate={endTime || SCHEDULE_MAX_DATE}
          ampm={false}
          minutesStep={15}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: 220 },
            },
          }}
        />

        {/* End Time */}
        <DateTimePicker
          label="End Time"
          value={endTime}
          onChange={handleEndChange}
          minDate={startTime || SCHEDULE_MIN_DATE}
          maxDate={SCHEDULE_MAX_DATE}
          ampm={false}
          minutesStep={15}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: 220 },
            },
          }}
        />

        {/* Duration Display */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 80 }}>
            {duration}
          </Typography>
        </Box>

        {/* Quick Duration Buttons */}
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => handleQuickDuration(1)}>1 Day</Button>
          <Button onClick={() => handleQuickDuration(3)}>3 Days</Button>
          <Button onClick={() => handleQuickDuration(7)}>7 Days</Button>
        </ButtonGroup>
      </Stack>
    </LocalizationProvider>
  );
};
